<template>
    <div class="dur-list-component">
        <!-- Card Mode (like verification page) -->
        <div class="dur-card-mode">
            <div v-for="(durs, kindKey) in groupByField(durItems, 'kind')" :key="kindKey" class="mb-3">
                <h6>{{ durKindTitle(kindKey.toString()) }}</h6>
                <div v-for="(groupedDurs, nameKey) in groupByField(durs, 'groupName')" :key="nameKey">
                    <strong v-if="groupedDurs[0].groupName">{{ nameKey }}</strong>
                    <div v-for="dur in groupedDurs" :key="dur.key" :class="{
                        'd-flex mb-3 dur-unit-wrapper': true,
                        'align-items-center': !requireNotes,
                        'inactive': dur.active === false
                    }" style="gap: 10px;">
                        <!-- Approval Switch -->
                        <div v-if="showApprovalSwitches && !readOnly && (requireExplicitDur === 0 || requireExplicitDur)"
                            style="min-width: 60px;">
                            <n-switch v-model="dur.status"
                                v-if="dur.active !== false && dur.severity >= requireExplicitDur"
                                :id="getDurSwitchId(dur)"
                                :class="{ 'mb-0': true, 'selected': isDurSwitchSelected(dur) }"
                                color="green"
                                @change="$emit('dur-status-change', dur)">
                                <template #on>
                                    <i class="fa fa-check" />
                                </template>
                                <template #off>
                                    <i class="fa fa-times" style="color:red;" />
                                </template>
                            </n-switch>
                        </div>

                        <!-- DUR Card -->
                        <div :class="'dur-unit flex-fill ' + durSeverityClass(dur.severity)">
                            <div class="icon">
                                <i :class="{
                                    'fa fa-fw': true,
                                    'fa-info': dur.severity === 0,
                                    'fa-exclamation-circle': dur.severity === 2,
                                    'fa-exclamation': dur.severity === 1
                                }" />
                            </div>
                            <div class="flex-fill">
                                <div class="dur-grid">
                                    <div class="font-weight-bold severity-label">
                                        <span>{{ dur.name }}</span>
                                    </div>
                                    <div v-if="dur.secondaryName" class="font-weight-bold">
                                        {{ dur.secondaryName }}
                                    </div>
                                    <div v-if="dur.ternaryName" class="font-weight-bold">
                                        {{ dur.ternaryName }}
                                    </div>
                                </div>
                                <div v-if="dur.description" class="mt-2">
                                    {{ dur.description }}
                                </div>
                                <div v-if="requireNotes && requireExplicitDur != null && dur.severity >= requireExplicitDur && !readOnly">
                                    <hr class="my-1">
                                    <small><strong>Notes</strong></small>
                                    <b-textarea v-model="dur.notes" size="sm" block-listener
                                        :readonly="dur.active === false || readOnly"
                                        @input="$emit('dur-notes-change', dur)" />
                                </div>
                                <div v-if="dur.active === false">
                                    <strong>Inactive (From previous DUR generation)</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Verification } from '@/models/Verification';
import NSwitch from '@/components/Switch.vue';

@Component({
    name: "DURListComponent",
    components: {
        NSwitch
    }
})
export default class DURListComponent extends Vue {
    @Prop({ required: true }) protected durItems!: Verification.DURItem[];
    @Prop({ default: false }) protected showApprovalSwitches!: boolean;
    @Prop({ default: null }) protected requireExplicitDur!: number | null;
    @Prop({ default: false }) protected requireNotes!: boolean;
    @Prop({ default: true }) protected groupByKind!: boolean;
    @Prop({ default: true }) protected groupByName!: boolean;
    @Prop({ default: false }) protected readOnly!: boolean;
    @Prop({ default: () => [] }) protected explicitDursRequiringAck!: Verification.DURItem[];
    @Prop({ default: 0 }) protected verificationIndex!: number;

    // Table field definitions
    interactionFields = [
        { key: 'name', label: 'Interaction', sortable: true },
        { key: 'severity', label: 'Severity', sortable: true },
        { key: 'description', label: 'Description', sortable: false }
    ];

    durFields = [
        { key: 'name', label: 'Issue', sortable: true },
        { key: 'kind', label: 'Type', sortable: true },
        { key: 'severity', label: 'Severity', sortable: true },
        { key: 'description', label: 'Description', sortable: false },
        { key: 'drugName', label: 'Drug', sortable: true }
    ];

    // Computed properties for table mode
    get interactions(): Verification.DURItem[] {
        if (!this.durItems?.length) return [];
        return this.durItems.filter((item: Verification.DURItem) =>
            item.kind === Verification.DURItemKind.drugInteraction
        );
    }

    get nonInteractionDurs(): Verification.DURItem[] {
        if (!this.durItems?.length) return [];
        return this.durItems.filter((item: Verification.DURItem) =>
            item.active && item.kind !== Verification.DURItemKind.drugInteraction
        );
    }

    // Helper methods
    groupByField(array: any[], field: string) {
        if (!array?.length) return {};
        const groupBy = (array: any[], property: string) => array.reduce((grouped: any, element: any) => ({
            ...grouped,
            [element[property]]: [...(grouped[element[property]] || []), element],
        }), {});

        return groupBy(array, field);
    }

    durSeverityClass(severity: number): string {
        if (severity === 1) return "moderate";
        if (severity === 2) return "major";
        return "";
    }

    durKindTitle(kind: string): string {
        if (kind === "0") return "Allergy";
        if (kind === "1") return "Duplicate Therapy";
        if (kind === "2") return "Interactions";
        if (kind === "3") return "Pregnancy";
        if (kind === "4") return "Disease";
        if (kind === "5") return "Dose";
        return "";
    }

    getSeverityVariant(severity: Verification.DURItemSeverityLevel): string {
        switch (severity) {
            case Verification.DURItemSeverityLevel.major: return 'danger';
            case Verification.DURItemSeverityLevel.moderate: return 'warning';
            case Verification.DURItemSeverityLevel.informational: return 'info';
            default: return 'secondary';
        }
    }

    getSeverityText(severity: Verification.DURItemSeverityLevel): string {
        switch (severity) {
            case Verification.DURItemSeverityLevel.major: return 'Major';
            case Verification.DURItemSeverityLevel.moderate: return 'Moderate';
            case Verification.DURItemSeverityLevel.informational: return 'Informational';
            default: return 'Unknown';
        }
    }

    getKindVariant(kind: Verification.DURItemKind): string {
        switch (kind) {
            case Verification.DURItemKind.drugInteraction: return 'danger';
            case Verification.DURItemKind.allergy: return 'warning';
            case Verification.DURItemKind.duplicateTherapy: return 'info';
            case Verification.DURItemKind.pregnancy: return 'warning';
            case Verification.DURItemKind.disease: return 'secondary';
            case Verification.DURItemKind.dose: return 'primary';
            default: return 'secondary';
        }
    }

    getKindText(kind: Verification.DURItemKind): string {
        switch (kind) {
            case Verification.DURItemKind.drugInteraction: return 'Drug Interaction';
            case Verification.DURItemKind.allergy: return 'Allergy';
            case Verification.DURItemKind.duplicateTherapy: return 'Duplicate Therapy';
            case Verification.DURItemKind.pregnancy: return 'Pregnancy';
            case Verification.DURItemKind.disease: return 'Disease';
            case Verification.DURItemKind.dose: return 'Dose';
            default: return 'Unknown';
        }
    }

    getDurSwitchId(dur: Verification.DURItem): string | null {
        const index = this.explicitDursRequiringAck.findIndex(d => d.key === dur.key);
        if (index === -1) return null;
        return `switch_dur_${index}`;
    }

    isDurSwitchSelected(dur: Verification.DURItem): boolean {
        const index = this.explicitDursRequiringAck.findIndex(d => d.key === dur.key);
        if (index === -1) return false;
        return this.verificationIndex === index + 6;
    }
}
</script>

<style scoped>
.text-wrap {
    word-wrap: break-word;
    white-space: normal;
}

.dur-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px;
}

.dur-unit {
    border: 1px solid gray;
    gap: 5px;
    display: flex;
    align-items: center;
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.02);
}

.dur-unit>* {
    padding: 5px;
}

.dur-unit .icon {
    background: gray;
    color: white;
    align-self: stretch;
    padding: 3px;
    font-size: 1.2em;
}

.dur-unit.moderate {
    border-color: orangered;
    background: rgba(255, 68, 0, 0.07);
}

.dur-unit.moderate .icon {
    background: orangered;
}

.dur-unit.major {
    border-color: red;
    background: rgba(255, 0, 0, 0.07);
}

.dur-unit.major .icon {
    background: red;
}

.dur-unit .icon i {
    position: sticky;
    top: 10px;
}

.dur-unit-wrapper.inactive {
    opacity: 0.6;
}
</style>
